<template>
  <div>
    <fieldset>
      <input
        type="hidden"
        :data-vv-scope="validationScope"
        :name="`${fieldValidationName}${moduleKey}`"
        :value="currentSelectionKey"
        v-validate.continues="`vehicle-level:${moduleKey}`"/>
      <fieldset>
        <template v-if="!hideMakeModel">
          <div v-if="!hideMake" class="tla-form-group">
            <label class="tla-form-group__label tla-form-group__label--required" :class="{ 'sr-only' : hideLabels }" v-html="vehicleMakeLabel"></label>
            <div v-if="questionLabels[0]">
              <label  class="tla-form-group__label"
                v-html="questionLabels[0]"></label>
            </div>
            <select
              id="ManufacturerIds"
              class="tla-form-group__input tla-form-group__input--arrow"
              :class="getMakeValidationClass('input')"
              v-validate.continues="'required'"
              :data-vv-name="manufacturerValidationTag"
              :data-vv-scope="validationScope"
              v-model="manufacturerIds"
              :disabled="disableManufacturerInput"
              name="manufacturerIds">
              <option selected disabled value="null">
                {{vehicleMakePlaceholder}}
              </option>
              <option
                v-for="manufacturer in manufacturers"
                :value="manufacturer.manufacturerId"
                :key="manufacturer.manufacturerId">
                {{manufacturer.manufacturerName}}
              </option>
            </select>
            <span class="tla-form-group__message tla-form-group__message--spaced" :class="getMakeValidationClass('message')">Please select a make.</span>
          </div>
          <div v-if="!hideModel" id="range-wp" class="tla-form-group">
            <label class="tla-form-group__label tla-form-group__label--required" :class="{ 'sr-only' : hideLabels }" v-html="vehicleModelLabel"></label>
            <div v-if="questionLabels[1]">
              <label  class="tla-form-group__label"
                v-html="questionLabels[1]"></label>
            </div>
            <select
              id="RangeIds"
              class="tla-form-group__input tla-form-group__input--arrow"
              :class="getRangeValidationClass('input')"
              v-validate.continues="'required|min:1'"
              :data-vv-name="rangeValidationTag"
              :data-vv-scope="validationScope"
              v-model="rangeId"
              :disabled="disableRangeInput"
              name="rangeIds">
              <option disabled selected value="null">
                {{vehicleModelPlaceholder}}
              </option>
              <option
                v-for="range in ranges"
                :value="range.rangeId"
                :key="range.rangeId">
                {{range.rangeName}}
              </option>
            </select>
            <span class="tla-form-group__message tla-form-group__message--spaced" :class="getRangeValidationClass('message')">Please select a model.</span>
          </div>
        </template>
      </fieldset>
      <fieldset v-if="useBodystyles" id="bodystyle-wp" class="tla-form-group" v-show="bodystyles !== null && bodystyles.length > 0 && !hideBodystylesIfOneOption">
        <label class="tla-form-group__label tla-form-group__label--required" :class="{ 'sr-only' : hideLabels }" v-html="vehicleBodystyleLabel"></label>

        <div class="tla-form-group__touchpoints">
          <form-touch-point
            v-for="(item, index) in bodystyles"
            :item-name="item"
            :hide-image="!hasBodyStyleIcon(item)"
            :index="index"
            :key="`${manufacturerName} ${rangeName} ${item}`"
            :collection-name="'bodystyles'"
            :moduleKey="`${moduleKey}`"
            :selected="(bodystyles.length < 2)"
            :itemText="formatBodystyle(item)"
            :validation-scope="validationScope"
            validation-rules=""
            :array-length="bodystyles.length"
            @bodystyles="updateBodystyle([$event])">
          </form-touch-point>

          <form-touch-point v-if="showNotSureBodyStyle"
            :itemName="`notSure-bodystyles`"
            :key="`${manufacturerName} ${rangeName}-notSure`"
            :collectionName="'bodystyles'"
            :collectionClassName="'not-sure'"
            :validationRules="''"
            :validationScope="validationScope"
            :itemText="`Not Sure`"
            @bodystyles="updateBodystyle(null, true)">
          </form-touch-point>
        </div>
        <span class="tla-form-group__message" :class="getBodyStyleValidationClass('message')">Please select an option.</span>
      </fieldset>
    </fieldset>
    </div>
  </template>

<script lang="ts">
import { Validator } from 'vee-validate';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import VueEvents from 'vue-events';
import VehicleLevel from '@/enums/VehicleLevel';
import Results from '@/models/vehicle/results';
import { stringHelperService } from '@/services/StringHelperService';
import { namespace } from 'vuex-class';
import Manufacturer from '../../models/vehicle/manufacturer';
import Range from '../../models/vehicle/range';
import { validationService } from '../../services/ValidationService';
import FormTouchPointComponent from '../ui/FormTouchPointComponent.vue';
import SearchQuery from '../../models/SearchQuery';
import IManufacturer from '../../models/vehicle/interfaces/manufacturer.d';
import IRange from '../../models/vehicle/interfaces/range.d';
import ExtraData from '../../models/ExtraData';

const bodyStylesWithIcons = [
  'citycar',
  'mpv',
  '4x4',
  'sports',
  'convertible',
  'coupe',
  'saloon',
  'estate',
  'hatchback'
];

// Custom vee-validate
Validator.extend('vehicle-level', {
  getMessage: (field: string) => 'Please select a vehicle',
  validate: (value: string, moduleKeys: any) => {
    const [moduleKey]: string = moduleKeys;
    return validationService.isVehicleLevelDataSet(moduleKey);
  }
});

Vue.use(VueEvents);

const InitialSettingsModule = namespace('initialSettingsModule');

@Component({
  components: {
    'form-touch-point': FormTouchPointComponent
  }
})
export default class VehicleLevelFilterComponent extends Vue {
  @InitialSettingsModule.Getter public initialSettings;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'Make' })
  public vehicleMakeLabel!: string;

  @Prop({ default: 'Please select a make' })
  public vehicleMakePlaceholder!: string;

  @Prop({ default: 'Model' })
  public vehicleModelLabel!: string;

  @Prop({ default: 'Please select a model' })
  public vehicleModelPlaceholder!: string;

  @Prop({ default: 'Bodystyle' })
  public vehicleBodystyleLabel!: string;

  @Prop({ default: false })
  public hideLabels!: boolean;

  @Prop({ default: false })
  public hideMakeModel!: boolean;

  @Prop({ default: false })
  public hideBodyStyles!: boolean;

  @Prop({ default: '' })
  public moduleKey!: string;

  @Prop({ default: 'vehicleSelectionValidation' })
  public fieldValidationName!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public allowNotSureBodyStyle!: boolean;

  @Prop({ default: false })
  public hideMake!: boolean;

  @Prop({ default: false })
  public hideModel!: boolean;

  @Prop({ default: false })
  public skipInitialisation!: boolean;

  @Prop({ default: () => [] })
  public questionLabels!: string[];

  public manufacturerValidationTag: string = 'manufacturerTag';

  public rangeValidationTag: string = 'rangeTag';

  private bodyStyleNotSure: boolean = false;

  // Vehicle Store

  public get currentRange(): IRange | null {
    return this.$store.getters[`vehicleModule${this.moduleKey}/currentRange`];
  }

  public get currentManufacturer(): IManufacturer | null {
    return this.$store.getters[`vehicleModule${this.moduleKey}/currentManufacturer`];
  }

  public get manufacturerName(): string {
    if (this.currentManufacturer) {
      return this.currentManufacturer.manufacturerName;
    }

    return '';
  }

  public get rangeName(): string {
    if (this.currentRange) {
      return this.currentRange.rangeName;
    }

    return '';
  }

  public get currentSelectionKey(): string {
    return this.$store.getters[`vehicleModule${this.moduleKey}/currentSelectionKey`];
  }

  public get searchQuery(): SearchQuery {
    return this.$store.getters[`searchQueryModule${this.moduleKey}/searchQuery`];
  }

  public get showNotSureBodyStyle(): boolean {
    return this.bodystyles.length > 1 && this.allowNotSureBodyStyle;
  }

  public hasBodyStyleIcon(value: string): boolean {
    const bodyStyle = stringHelperService.classifyString(value);
    return bodyStylesWithIcons.includes(bodyStyle);
  }

  public async submitManufacturer(manufacturerId: number | null) {
    let manufacturer: IManufacturer = new Manufacturer();
    if (manufacturerId !== null && this.manufacturers !== null) {
      [manufacturer] = this.manufacturers.filter((d) => d.manufacturerId === manufacturerId);
    }

    return this.$store.dispatch(`vehicleModule${this.moduleKey}/submitManufacturer`, manufacturer);
  }

  public async submitRange(rangeId: number | null) {
    let range: IRange = new Range();
    if (rangeId !== null && this.ranges !== null) {
      [range] = this.ranges.filter((d) => d.rangeId === rangeId);
    }

    return this.$store.dispatch(`vehicleModule${this.moduleKey}/submitRange`, range);
  }

  public async submitBodystyle(bodystyle: string[] | null) {
    this.$store.dispatch(`vehicleModule${this.moduleKey}/submitBodystyle`, bodystyle);
  }

  public async submitResetVehicleCustomisationData() {
    await this.$store.dispatch(`vehicleModule${this.moduleKey}/submitResetVehicleCustomisationData`);
  }

  public async submitResetResultsAndFilteredResults() {
    await this.$store.dispatch(`vehicleSearchModule${this.moduleKey}/submitResetResultsAndFilteredResults`);
  }

  // Vehicle Search Store

  public updateFilteredResults(): Promise<any> {
    return this.$store.dispatch(`vehicleSearchModule${this.moduleKey}/updateFilteredResults`);
  }

  public updateResults(): Promise<Results> {
    return this.$store.dispatch(`vehicleSearchModule${this.moduleKey}/updateResults`);
  }

  public get manufacturers(): IManufacturer[] | null {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/manufacturers`];
  }

  public get ranges(): IRange[] | null {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/ranges`];
  }

  public get bodystyles(): string[] {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/bodystyles`];
  }

  public get doors(): string[] | null {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/doors`];
  }

  public get disableManufacturerInput(): boolean {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/disableManufacturerInput`];
  }

  public get disableRangeInput(): boolean {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/disableRangeInput`];
  }

  // Component
  @Watch('manufacturerIds')
  public onManufacturersChanged(val: number[], oldVal: number[]) {
    if (val === null) {
      this.$validator.reset();
    }
  }

  @Watch('currentSelectionKey')
  public validateSelectedOptions() {
    this.validateComponent();
  }

  get hideBodyStyleOption(): boolean {
    return this.bodystyles.length <= 1;
  }

  get hideBodystylesIfOneOption(): boolean {
    return this.initialSettings.hideFieldsWithOneOption && this.hideBodyStyleOption;
  }

  public getMakeValidationClass(fieldType: string) {
    if (this.manufacturerIds !== null && this.manufacturerIds > 0) {
      return '';
    }

    return this.getValidationClass(`${this.fieldValidationName}${this.moduleKey}`, fieldType);
  }

  public getRangeValidationClass(fieldType: string) {
    if (this.rangeId !== null && this.rangeId > 0) {
      return '';
    }

    return this.getValidationClass(`${this.fieldValidationName}${this.moduleKey}`, fieldType);
  }

  public getBodyStyleValidationClass(fieldType: string) {
    if ((this.bodystyle !== null && this.bodystyle.length > 0) || this.bodyStyleNotSure) {
      return '';
    }

    return this.getValidationClass(`${this.fieldValidationName}${this.moduleKey}`, fieldType);
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public get useBodystyles(): boolean {
    return (this.searchQuery.vehicleLevel === VehicleLevel.Derivative || this.searchQuery.vehicleLevel === VehicleLevel.Trim) && !this.hideBodyStyles;
  }

  get manufacturerIds(): number | null {
    const { manufacturerIds } = this.searchQuery;
    return manufacturerIds !== null && manufacturerIds.length === 1 ? manufacturerIds[0] : null;
  }

  set manufacturerIds(manufacturerId: number | null) {
    this.$validator.reset();
    this.$events.emit('triggerCustomisationValidationReset');

    this.submitManufacturer(manufacturerId);
    this.validateComponent();
  }

  get rangeId(): number | null {
    return this.searchQuery.rangeIds;
  }

  set rangeId(rangeId: number | null) {
    this.submitRange(rangeId);
    this.validateComponent();
  }

  get bodystyle(): string[] | null {
    return this.searchQuery.bodystyles;
  }

  public async updateBodystyle(value: string[] | null, isNotSure: boolean = false) {
    if (this.moduleKey.length === 0) {
      if (isNotSure) {
        const extraData: ExtraData = new ExtraData('bodyStyleNotSure', 'true');
        await this.$store.dispatch('enquiryModule/submitExtraData', extraData);
      } else {
        this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'bodyStyleNotSure');
      }
    }

    this.bodyStyleNotSure = isNotSure;
    await this.submitResetVehicleCustomisationData();
    await this.submitBodystyle(value);
    await this.updateFilteredResults();
    this.$events.emit('vehicleLevelSet', this.moduleKey);
  }

  public formatBodystyle(value: string) {
    return value === 'Sports' ? 'Sports/ Convertible' : value;
  }

  public validateComponent() {
    this.$validator.validateAll(this.validationScope);
  }

  @Watch('rangeId')
  public async setResults() {
    // Reset customisation data in the case that a user navigates back to step 1 and changes their vehicle.
    await this.submitResetResultsAndFilteredResults();
    await this.submitResetVehicleCustomisationData();
    let result = await this.updateResults();

    // Extra safety net if reset fails to fully reset.
    if (result === null || result.items.length === 0) {
      await this.submitResetVehicleCustomisationData();
      result = await this.updateResults();
    }

    if (this.useBodystyles) {
      if (result !== null && result.bodystyles.length === 1) {
        this.updateBodystyle(result.bodystyles);
        await this.updateFilteredResults();
      } else {
        this.updateBodystyle(null);
      }
    }

    if (this.hideBodyStyles) {
      if (result !== null && result.bodystyles.length === 1) {
        this.updateBodystyle(result.bodystyles);
      } else {
        this.updateBodystyle(null, true);
      }

      await this.updateFilteredResults();
    }

    this.$events.emit('vehicleRangeChanged', this.moduleKey);
  }

  @Watch('bodystyle')
  public bodyStyleChanged() {
    if (this.bodystyle === null && this.bodystyles.length === 1) {
      this.updateBodystyle(this.bodystyles);
    }
  }

  public get validationScopeName(): string {
    return `${this.validationScope}-instance${this.moduleKey}`;
  }

  public mounted() {
    this.validateComponent();
  }

  public async created() {
    if (!this.skipInitialisation) {
      await this.$store.dispatch(`vehicleSearchModule${this.moduleKey}/updateManufacturers`);
    }

    this.$validator.errors.remove('vehicle-customisation', this.validationScope);
    this.validateComponent();
  }
}
</script>
